import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import { Editor } from "@tinymce/tinymce-react";
import RichTextEditor, { factsheetPreset } from "Components/RichTextEditor";
import FullscreenToggle from "../Components/FullscreenToggle";
import useEditorFeatures, {
  EditorStatus,
  printDocument,
  useProofread,
} from "../Components/EditorFeatures";
import { DownloadFile } from "./FileApi";
import { openTrustedUrl } from "../urlSafety";
import Swal from "sweetalert2";

import { getFactsheetHtml, updateFactsheetHtml } from "../service";
import FormError from "../Components/FormError";
import { TINYMCE_API_KEY } from "../tinymceConfig";
import { isHtmlBlank } from "../Components/Notice/noticeHtml";
import {
  splitFactsheetDocument,
  joinFactsheetDocument,
  stripUnsafeFactsheetHtml,
  buildFactsheetEditorInit,
  humanizeFactsheetType,
  scopeFactsheetCss,
} from "./factsheetHtml";

const USE_TIPTAP = true;

const FACTSHEET_TOOLBAR =
  "undo redo | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | link | table | removeformat";

// Everything the shared editor offers a factsheet - see
// Components/RichTextEditor/features.js.
const FACTSHEET_FEATURES = {
  pageBreakButtons: true,
  typingShortcuts: true,
  dictation: true,
  saveShortcut: true,
  printShortcut: true,
  wordCount: true,
  justifyIndent: true,
};

// The factsheet's own stylesheet is confined to this selector - TinyMCE had an
// iframe to isolate it, Tiptap does not. See scopeFactsheetCss.
const FACTSHEET_SCOPE = ".rte--factsheet .ProseMirror";

const useStyles = makeStyles(() => ({
  dialogPaper: {
    height: "92vh",
    maxHeight: "92vh",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    padding: "1.5rem",
    backgroundColor: "#f1f1f4",
    scrollbarGutter: "stable",
  },
  sheet: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: "320px",
    backgroundColor: "#fff",
    border: "1px solid #d8d8d8",
    boxShadow: "0 1px 4px rgba(0,0,0,0.14)",
    // TinyMCE writes a pixel height onto its own container; as a flex item that
    // is only the flex basis, so the editor grows into the dialog on a tall
    // screen and shrinks on a short one instead of forcing a second scrollbar.
    "& .tox-tinymce": {
      flex: "1 1 auto",
      border: "none",
      borderRadius: 0,
    },
    // The same treatment for Tiptap. It has no inner iframe, so the scrolling
    // element is the content div rather than the editor shell.
    "& .rte": {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minHeight: 0,
      border: "none",
      borderRadius: 0,
    },
    "& .rte__content": {
      flex: "1 1 auto",
      minHeight: 0,
      overflowY: "auto",
      padding: "0.5in 0.6in",
    },
  },
  centred: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "1 1 auto",
    padding: "2rem",
  },
  typeChip: {
    height: "24px",
    fontSize: "0.75rem",
    color: TEXT.brand,
    backgroundColor: "#f3ecf6",
  },
  fileName: {
    maxWidth: "38ch",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actionArea: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    padding: "0.75rem 1.5rem",
    borderTop: "1px solid #eee",
  },
  actionGroup: {
    display: "flex",
    gap: "10px",
  },
  hint: {
    fontSize: "0.75rem",
    color: TEXT.muted,
  },
}));

// The backend puts its reason in the `error` field; anything else is ours to phrase.
const readApiError = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return data?.error || data?.message || fallback;
};

/**
 * Edits a generated factsheet as HTML.
 *
 * Opened from the file row's three-dots menu for files the backend flags
 * `editable`. Saving re-renders the PDF server side and keeps the same file id,
 * so the caller only has to refresh the listing to pick up the new version.
 */
export default function FactsheetEditor({
  open,
  onClose,
  fileId,
  fileName,
  onSaved,
  // The case this document belongs to: its id for dictation, its number
  // ("AC No. 12/2026") for the top of each page Page break / New page adds.
  disputeId,
  caseNumber,
}) {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const [factsheetType, setFactsheetType] = useState("");
  const [shell, setShell] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [css, setCss] = useState("");
  // TinyMCE reads initialValue once, so the editor is keyed and remounted
  // whenever the underlying content is replaced.
  const [editorVersion, setEditorVersion] = useState(0);

  const [bodyDraft, setBodyDraft] = useState("");

  const editorRef = useRef(null);

  useEffect(() => {
    if (!open || !fileId) return undefined;

    // A slow fetch that resolves after the dialog was closed must not write
    // into a reopened one.
    let cancelled = false;

    setLoading(true);
    setError("");
    setLoadError("");
    setDirty(false);
    setFactsheetType("");
    setShell("");
    setBodyHtml("");
    setCss("");

    getFactsheetHtml(fileId)
      .then((res) => {
        if (cancelled) return;
        const content = res?.data?.content || "";
        const parts = splitFactsheetDocument(content);
        setFactsheetType(res?.data?.factsheet_type || "");
        setShell(parts.shell);
        setBodyHtml(parts.bodyHtml);
        setCss(parts.css);
        setEditorVersion((version) => version + 1);
        if (!content) {
          setLoadError("This document came back empty and cannot be edited.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error loading factsheet HTML:", err);
        setLoadError(
          readApiError(err, "This document could not be loaded for editing.")
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, fileId]);

  const safeBodyHtml = useMemo(
    () => stripUnsafeFactsheetHtml(bodyHtml),
    [bodyHtml]
  );
  const editorInit = useMemo(() => buildFactsheetEditorInit(css), [css]);

  // safeBodyHtml only changes when a different factsheet loads, never while
  // typing, so this does not fight the user's edits.
  useEffect(() => {
    setBodyDraft(safeBodyHtml);
  }, [safeBodyHtml]);

  // The document's own CSS, rewritten to apply only inside the editor.
  const scopedCss = useMemo(
    () => scopeFactsheetCss(css, FACTSHEET_SCOPE),
    [css]
  );

  const editable = Boolean(bodyHtml) && !loadError;

  // Unsaved edits would otherwise disappear silently on close - confirm first.
  const requestClose = useCallback(async () => {
    if (!dirty) {
      onClose();
      return;
    }

    const result = await Swal.fire({
      title: "Discard changes?",
      text: "Your edits to this document have not been saved. They will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Discard",
      cancelButtonText: "Keep editing",
      reverseButtons: true,
    });

    if (result.isConfirmed) onClose();
  }, [dirty, onClose]);

  // Save Changes saves and closes, as it always has. Save Draft and Download
  // save and stay open ({ stayOpen: true }). Resolves true when it saved.
  const handleSave = async ({ stayOpen = false } = {}) => {
    if (!fileId) return false;
    if (!USE_TIPTAP && !editorRef.current) return false;

    const editedBody = USE_TIPTAP ? bodyDraft : editorRef.current.getContent();
    if (isHtmlBlank(editedBody)) {
      setError("The document cannot be empty.");
      return false;
    }

    setError("");
    setSaveMessage("");
    setSaving(true);
    try {
      const content = joinFactsheetDocument(
        shell,
        stripUnsafeFactsheetHtml(editedBody)
      );
      await updateFactsheetHtml(fileId, content);

      if (stayOpen) {
        setDirty(false);
        setSaving(false);
        setSaveMessage("Saved. The PDF has been regenerated from your edits.");
        if (onSaved) onSaved();
        return true;
      }

      // Every state update happens before onClose: the parent unmounts this
      // dialog on close, so anything set afterwards would land on a dead
      // component.
      setDirty(false);
      setSaving(false);
      // The file id is unchanged, so refreshing the listing is all the caller
      // has to do to pick up the regenerated PDF.
      if (onSaved) onSaved();
      onClose();
      Swal.fire(
        "Success",
        "Your changes to this document have been saved.",
        "success"
      );
      return true;
    } catch (err) {
      console.error("Error saving factsheet HTML:", err);
      setSaving(false);
      setError(readApiError(err, "An error occurred while saving your changes."));
      return false;
    }
  };

  // Print what is on screen: the document with its own stylesheet, edits
  // included.
  const handlePrint = () => {
    const body = stripUnsafeFactsheetHtml(bodyDraft);
    const full = joinFactsheetDocument(shell, body);
    printDocument({
      html: full,
      css: css,
      // A little room between the text and the cell borders.
      extraCss: "td, th { padding: 3pt 5pt; }",
      title: fileName || "Document",
    }).catch((err) => {
      console.error("Failed to open the print window:", err);
      setError("Could not open the print window. Please try again.");
    });
  };

  // The PDF of what is on screen: unsaved edits are saved (and the PDF
  // regenerated) first, as the order sheet's Download does.
  const handleDownload = async () => {
    if (dirty && !(await handleSave({ stayOpen: true }))) return;
    try {
      const res = await DownloadFile(fileId);
      openTrustedUrl(res.data.url, {
        onError: (message) => setError(message),
        context: "file download",
      });
    } catch (err) {
      console.error("Error downloading factsheet:", err);
      setError("Download failed. Please try again.");
    }
  };

  const proofread = useProofread({
    disputeId,
    getHtml: () => bodyDraft,
    setHtml: (html) => {
      setBodyDraft(html);
      setDirty(true);
    },
  });

  const { editorProps, dictation } = useEditorFeatures({
    features: FACTSHEET_FEATURES,
    preset: factsheetPreset,
    toolbar: FACTSHEET_TOOLBAR,
    caseNumber,
    disputeId,
    // Ctrl+S: what Save Changes does, with its guards.
    onSave: () => {
      if (!saving && !loading && editable) handleSave();
    },
    onPrint: handlePrint,
  });

  const typeLabel = humanizeFactsheetType(factsheetType);

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullscreen}
      classes={fullscreen ? undefined : { paper: classes.dialogPaper }}
      disableBackdropClick={dirty}
      disableEnforceFocus
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            style={{ gap: "10px", minWidth: 0 }}
          >
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Edit Document
            </Typography>
            {typeLabel && (
              <Chip
                className={classes.typeChip}
                size="small"
                icon={
                  <DescriptionOutlinedIcon style={{ fontSize: "0.9rem" }} />
                }
                label={typeLabel}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            {editable && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={proofread.state === "loading" || saving || loading}
                  onClick={proofread.run}
                  style={{ textTransform: "none", minWidth: "110px" }}
                >
                  {proofread.state === "loading" ? (
                    <Box display="flex" alignItems="center" style={{ gap: "0.4rem" }}>
                      <CircularProgress size={13} color="inherit" />
                      <span>Reviewing...</span>
                    </Box>
                  ) : (
                    "AI Proofread"
                  )}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={saving || loading}
                  onClick={() => handleSave({ stayOpen: true })}
                  style={{ textTransform: "none" }}
                >
                  Save Draft
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={saving || loading}
                  onClick={handleDownload}
                  style={{ textTransform: "none" }}
                >
                  Download
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handlePrint}
                  style={{ textTransform: "none" }}
                >
                  Print
                </Button>
              </>
            )}
            <FullscreenToggle
              fullscreen={fullscreen}
              onToggle={() => setFullscreen((value) => !value)}
            />
            <Button variant="outlined" size="small" onClick={requestClose}>
              Close
            </Button>
          </Box>
        </Box>
        {fileName && (
          <Typography
            className={`${classes.hint} ${classes.fileName}`}
            title={fileName}
          >
            {fileName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        {loading && (
          <Box className={classes.centred}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && editable && (
          <Box className={classes.sheet}>
            {USE_TIPTAP ? (
              <>
                {/* The document's stylesheet, confined to the editor. TinyMCE
                    had an iframe for this; without the rewrite these rules
                    would restyle the whole application. */}
                {scopedCss && (
                  <style dangerouslySetInnerHTML={{ __html: scopedCss }} />
                )}
                <RichTextEditor
                  // Keyed on the file only - Tiptap picks up replaced content
                  // through `value`, so editorVersion is not needed here.
                  key={fileId || "factsheet"}
                  className="rte--factsheet"
                  value={bodyDraft}
                  onChange={(html) => {
                    setBodyDraft(html);
                    setDirty(true);
                  }}
                  {...editorProps}
                  minHeight={0}
                />
              </>
            ) : (
              <Editor
                key={`${fileId || "factsheet"}-${editorVersion}`}
                apiKey={TINYMCE_API_KEY}
                onInit={(evt, editor) => {
                  editorRef.current = editor;
                }}
                initialValue={safeBodyHtml}
                init={editorInit}
                onDirty={() => setDirty(true)}
              />
            )}
          </Box>
        )}

        {!loading && !editable && (
          <Box className={classes.centred}>
            <Typography color="textSecondary">
              {loadError || "No editable content available for this document."}
            </Typography>
          </Box>
        )}

        <FormError message={error} />
      </DialogContent>

      <DialogActions className={classes.actionArea}>
        <EditorStatus
          dictation={dictation}
          proofread={proofread}
          saved={saveMessage}
          fallback={
            <Typography className={classes.hint} component="span">
              {editable ? "Saving re-generates the PDF from your edits." : ""}
            </Typography>
          }
        />

        <Box className={classes.actionGroup}>
          <Button variant="outlined" disabled={saving} onClick={requestClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={saving || loading || !editable}
            onClick={handleSave}
          >
            {saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Save Changes"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
